<template>
  <section class="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 class="text-lg font-semibold text-ink">Edit package</h2>
        <p class="text-sm text-muted">Update package details and pricing.</p>
      </div>
      <RouterLink to="/dashboard/packages" class="text-xs font-semibold text-brand">Back to packages</RouterLink>
    </div>

    <form class="mt-6 grid gap-4 sm:grid-cols-2" @submit.prevent="handleSubmit">
      <label class="block">
        <span class="text-xs font-semibold uppercase tracking-wide text-muted">Title</span>
        <input v-model.trim="form.title" type="text" class="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-ink shadow-sm focus:border-brand focus:outline-none">
      </label>
      <label class="block sm:col-span-2">
        <span class="text-xs font-semibold uppercase tracking-wide text-muted">Description</span>
        <textarea v-model.trim="form.description" rows="3" class="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-ink shadow-sm focus:border-brand focus:outline-none"></textarea>
      </label>
      <label class="block">
        <span class="text-xs font-semibold uppercase tracking-wide text-muted">Price</span>
        <input v-model.number="form.price" type="number" class="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-ink shadow-sm focus:border-brand focus:outline-none">
      </label>
      <label class="block">
        <span class="text-xs font-semibold uppercase tracking-wide text-muted">Price per</span>
        <select v-model="form.price_per" class="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-ink shadow-sm focus:border-brand focus:outline-none">
          <option value="per_day">per_day</option>
          <option value="per_month">per_month</option>
          <option value="per_event">per_event</option>
          <option value="per_request">per_request</option>
          <option value="on_request">on_request</option>
        </select>
      </label>
      <label class="block">
        <span class="text-xs font-semibold uppercase tracking-wide text-muted">Status</span>
        <select v-model="form.status" class="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-ink shadow-sm focus:border-brand focus:outline-none">
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </label>
      <p v-if="error" class="sm:col-span-2 text-xs font-semibold text-red-600">{{ error }}</p>
      <div class="flex flex-wrap gap-3 sm:col-span-2">
        <button type="submit" class="rounded-full bg-brand px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-brandDark" :disabled="loading">
          {{ loading ? 'Saving...' : 'Save changes' }}
        </button>
        <button type="button" class="rounded-full border border-red-200 px-4 py-2 text-xs font-semibold text-red-600 transition hover:border-red-300" :disabled="deleting" @click="handleDelete">
          {{ deleting ? 'Deleting...' : 'Delete package' }}
        </button>
      </div>
    </form>
  </section>
</template>

<script setup>
import { reactive, ref, onMounted, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import api from '@/services/api'
import { getAuthUserId, setAuthSession, setAuthUser } from '@/services/auth'

const route = useRoute()
const router = useRouter()
const loading = ref(false)
const deleting = ref(false)
const loadingPackage = ref(false)
const error = ref('')

const form = reactive({
  id: route.params.id || '',
  title: '',
  description: '',
  price: '',
  price_per: 'per_event',
  status: 'active'
})

const loadPackage = async () => {
  let vendorId = getAuthUserId()
  if (!vendorId) {
    try {
      const profilePayload = new FormData()
      const fallbackVendorId = route.query.vendor_id || route.query.vendorId || ''
      if (fallbackVendorId) {
        profilePayload.append('vendor_id', fallbackVendorId)
      }
      const { data } = await api.getProfileVendor(profilePayload)
      const user = data?.user || data?.data?.user || data?.data || data?.profile || null
      if (user) {
        const resolvedVendorId =
          user?.s_id ||
          user?.id ||
          user?.user_id ||
          user?.vendor_id ||
          user?.vendorId ||
          fallbackVendorId
        setAuthUser({
          ...user,
          s_id: resolvedVendorId
        })
        setAuthSession({
          loggedIn: true,
          s_id: resolvedVendorId
        })
      }
    } catch {
      // ignore profile fetch errors
    }
    vendorId = getAuthUserId()
  }
  if (!vendorId) {
    error.value = 'Unable to load packages: missing vendor id.'
    return
  }
  loadingPackage.value = true
  error.value = ''
  const payload = new FormData()
  payload.append('vendor_id', vendorId)
  try {
    const { data } = await api.listVendorPackages(payload)
    const list = data?.list || data?.data || data?.packages || []
    const matchId = route.params.id?.toString()
    const pkg = list.find((item) => (item.id || item.package_id || '').toString() === matchId)
    if (pkg) {
      form.id = pkg.id || pkg.package_id || form.id
      form.title = pkg.title || ''
      form.description = pkg.description || ''
      form.price = pkg.price || ''
      form.price_per = pkg.price_per || 'per_event'
      form.status = pkg.status || 'active'
    } else {
      error.value = 'Package not found for this vendor.'
    }
  } catch (err) {
    error.value = err?.response?.data?.message || err?.message || 'Failed to load package details'
  } finally {
    loadingPackage.value = false
  }
}

const handleSubmit = async () => {
  error.value = ''
  loading.value = true
  const payload = new FormData()
  Object.entries(form).forEach(([key, value]) => payload.append(key, value ?? ''))

  try {
    await api.updateVendorPackage(payload)
    router.push('/dashboard/packages')
  } catch (err) {
    error.value = err?.response?.data?.message || err?.message || 'Failed to update package'
  } finally {
    loading.value = false
  }
}

const handleDelete = async () => {
  deleting.value = true
  error.value = ''
  const payload = new FormData()
  payload.append('id', form.id)

  try {
    await api.deleteVendorPackage(payload)
    router.push('/dashboard/packages')
  } catch (err) {
    error.value = err?.response?.data?.message || err?.message || 'Failed to delete package'
  } finally {
    deleting.value = false
  }
}

onMounted(() => {
  loadPackage()
})

watch(
  () => route.params.id,
  () => {
    loadPackage()
  }
)
</script>
